import { AnimatePresence, motion } from "framer-motion";
import { useCallback } from "react";
import {
  MdClose,
  MdMoreHoriz,
  MdDeleteOutline,
  MdOutlineDriveFileRenameOutline,
  MdOutlineDownload,
} from "react-icons/md";

interface OptionsMenuProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setRenameSignal: React.Dispatch<React.SetStateAction<boolean>>;
  handleDeleteOption?: () => void;
  handleDownloadOption?: () => void;
}

const OptionsMenu: React.FC<OptionsMenuProps> = ({
  open,
  setOpen,
  setRenameSignal,
  handleDeleteOption,
  handleDownloadOption,
}) => {
  /**
   * Function to toggle the behaviour of the menu
   */
  const handleOpenClick = useCallback(() => {
    // We use the negative here because the value is modified once
    // clicked, so before open it's closed and viceversa.
    setOpen(!open);
  }, [open, setOpen]);

  /**
   * List that defines the options of the dropdown list
   */
  const options = [
    {
      text: "Delete",
      icon: <MdDeleteOutline className="w-6 h-6" />,
      onClickFunction: handleDeleteOption,
    },
    {
      text: "Rename",
      icon: <MdOutlineDriveFileRenameOutline className="w-6 h-6" />,
      onClickFunction: () => setRenameSignal((current) => !current),
    },
    {
      text: "Download",
      icon: <MdOutlineDownload className="w-6 h-6" />,
      onClickFunction: handleDownloadOption,
    },
  ];

  return (
    <div className="relative flex">
      <button
        className="flex justify-center rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-white/75 cursor-pointer z-20"
        title="Options"
        onClick={handleOpenClick}
      >
        <div className="relative flex justify-center items-center p-2 w-11 h-11 bg-neutral-450 hover:bg-neutral-600 ease-in-out duration-150 rounded-full backdrop-blur-[32px]">
          <AnimatePresence>
            {open && (
              <motion.div
                key="close"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{
                  opacity: 0,
                  scale: 0.95,
                  transition: { duration: 0.3, ease: "easeIn" },
                }}
                transition={{ duration: 0.5, ease: "easeOut" }}
                className="absolute flex w-8 h-8 justify-center items-center"
              >
                <MdClose className="w-6 h-6 text-secondary-50" />
              </motion.div>
            )}
            {!open && (
              <motion.div
                key="open"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{
                  opacity: 0,
                  scale: 0.95,
                  transition: { duration: 0.3, ease: "easeIn" },
                }}
                transition={{ duration: 0.5, ease: "easeOut" }}
                className="absolute flex w-8 h-8 justify-center items-center"
              >
                <MdMoreHoriz className="w-6 h-6 text-secondary-50" />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <AnimatePresence>
          {open && (
            <motion.div
              key="dropdown"
              initial={{ opacity: 0, scale: 0.9, y: "40%" }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{
                opacity: 0,
                scale: 0.95,
                transition: { duration: 0.3, ease: "easeIn" },
              }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              className="absolute inline-flex flex-col gap-2 z-30 right-0 mt-2 top-10 w-fit h-fit rounded-lg justify-center items-center"
            >
              {options.map((option, index: number) => {
                return (
                  <div
                    key={index}
                    className="bg-white text-neutral-700 hover:bg-neutral-100 ease-in-out duration-150 w-full h-[45px] py-2 px-5 rounded-lg justify-left items-center gap-2 inline-flex"
                    onClick={option.onClickFunction}
                  >
                    <div className="w-6 h-6 relative">{option.icon}</div>
                    <div className="text-base font-normal font-sans leading-7 select-none">
                      {option.text}
                    </div>
                  </div>
                );
              })}
            </motion.div>
          )}
        </AnimatePresence>
      </button>
    </div>
  );
};

export default OptionsMenu;
